import React, { useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import produce from "immer";
import { useDebounce } from "use-lodash-debounce";
import {
  Wrapper,
  Input,
  Select,
  Checkbox,
  ButtonsWrapper,
  AddButton,
  Label,
} from "./styled";
import VerticalTable from "./tables/VerticalTable";
import HorizontalTable from "./tables/HorizontalTable";
import { checkIfSmallScreen } from "./helpers/checkIfSmallScreen";
import SettingsModal from "./editComponents/SettingsModal";
const widths = {
  default: 220,
  number: 110,
  select: 180,
  checkbox: 20,
  editButton: 40,
};

const InputTable = (props) => {
  const {
    builder = false,
    extra = {},
    id,
    save = () => {},
    value,
    validationError = {},
    intl,
    disabled = false,
  } = props;
  const {
    columns = [],
    defaultRows = [],
    dynamicRows = false,
    addRowText = "",
    label,
    enableCellLock = false,
  } = extra;

  const [mounted, setMounted] = useState(false);
  const [rows, setRows] = useState(value || defaultRows || []);
  const [isSmallScreen, setIsSmallScreen] = useState(checkIfSmallScreen());
  const debouncedRows = useDebounce(rows, 500, { trailing: true });

  const [activeRow, setActiveRow] = useState(null);

  const openSettings = (rowIdx) => {
    setActiveRow({ rowIdx, data: rows[rowIdx] });
  };

  const hasRequiredColumn = columns.some((col) => col.required);

  useEffect(() => {
    if (mounted) {
      if (builder) {
        save({ _id: id, key: "defaultRows", value: debouncedRows });
        return;
      }
      saveToDb(columns);
      save({ key: id, value: debouncedRows });
    }
  }, [debouncedRows]);

  function saveToDb(columns) {
    if (columns.some((el) => el.saveToDb)) {
      const sumObj = {};
      columns.forEach((columns, index) => {
        if (columns.saveToDb) {
          let sum = 0;
          rows.forEach((row) => {
            row.columns.forEach((col) => {
              if (col.columnId === columns.id) {
                sum += Number(col.value);
              }
            });
          });
          sumObj[index + 1] = sum;
        }
      });
      save({ key: id + "-sum", value: sumObj });
    } else {
      return;
    }
  }

  useEffect(() => {
    if (
      builder &&
      mounted &&
      rows.length &&
      columns.length !== rows[0].columns.length
    ) {
      setRows(
        produce((draft) => {
          if (columns.length > rows[0].columns.length) {
            draft.map(({ columns: cols }) =>
              cols.push({ columnId: [...columns].pop().id, value: "" })
            );
          } else {
            const columnIds = columns.map(({ id }) => id);
            const colIdx = rows[0].columns.findIndex(
              ({ columnId }) => !columnIds.includes(columnId)
            );
            draft.map(({ columns: cols }) => cols.splice(colIdx, 1));
          }
        })
      );
    }
  }, [columns]);

  useEffect(() => {
    setMounted(true);

    window.addEventListener("resize", evaluateScreenSize);

    return () => window.removeEventListener("resize", evaluateScreenSize);
  }, []);

  const evaluateScreenSize = () => {
    const isSmallScreen = checkIfSmallScreen();
    setIsSmallScreen(isSmallScreen);
  };

  const renderInput = (
    type,
    value,
    rowIdx,
    colIdx,
    vertical,
    disabled = false
  ) => {
    const isLocked = rows[rowIdx].columns[colIdx].locked || false;

    switch (type) {
      case "checkbox":
        return (
          <Checkbox
            checked={value}
            disabled={disabled || isLocked}
            onClick={
              !disabled && !isLocked
                ? () => onChange(!value, rowIdx, colIdx)
                : null
            }
          >
            <input
              type="checkbox"
              checked={value}
              disabled={disabled || isLocked}
              onChange={() => {}}
            />
          </Checkbox>
        );
      case "select":
        return (
          <Select
            value={value}
            onChange={(e) => onChange(e.target.value, rowIdx, colIdx)}
            disabled={disabled || isLocked}
          >
            {columns[colIdx].options.map((option, i) => (
              <option key={i} value={option}>
                {option}
              </option>
            ))}
          </Select>
        );
      default:
        return (
          <Input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value, rowIdx, colIdx)}
            disabled={disabled || isLocked}
          />
        );
    }
  };

  const onChange = (value, rowIdx, colIdx) => {
    setRows(
      produce((draft) => {
        draft[rowIdx].columns[colIdx].value = value;
      })
    );
  };

  const addRow = () => {
    const newRow = {
      locked: false,
      columns: Array(columns.length)
        .fill("")
        .map((value, i) => ({
          columnId: columns[i].id,
          columnName: columns[i].label,
          value,
        })),
    };

    setRows(
      produce((draft) => {
        draft.push(newRow);
      })
    );
  };

  const deleteRow = (rowIdx) => {
    setRows(
      produce((draft) => {
        draft.splice(rowIdx, 1);
      })
    );
  };

  const sumColumn = (colIdx) => {
    return rows.reduce(
      (acc, cur) => acc + Number(cur.columns[colIdx].value),
      0
    );
  };

  const saveSettings = ({ rowIdx, data: updatedRow }) => {
    setRows(
      produce((draft) => {
        draft[rowIdx] = {
          ...draft[rowIdx],
          ...updatedRow,
        };
      })
    );
    setActiveRow(null);
  };

  const toggleLockRow = (rowIdx) => {
    setRows(
      produce((draft) => {
        draft[rowIdx].locked = !draft[rowIdx].locked;
      })
    );
  };

  const toggleLockCell = (rowIdx, colIdx) => {
    setRows(
      produce((draft) => {
        draft[rowIdx].columns[colIdx].locked =
          !draft[rowIdx].columns[colIdx].locked;
      })
    );
  };

  if (!columns.length) {
    return null;
  }

  return (
    <Wrapper
      className={hasRequiredColumn ? "sp-required InputTable" : "InputTable"}
    >
      <Label>{label ? label : ""}</Label>
      {!builder && isSmallScreen ? (
        <VerticalTable
          columns={columns}
          rows={rows}
          widths={widths}
          validationError={validationError}
          renderInput={renderInput}
          deleteRow={deleteRow}
          builder={builder}
          toggleLockRow={toggleLockRow}
          sumColumn={sumColumn}
          disabled={disabled}
        />
      ) : (
        <HorizontalTable
          columns={columns}
          rows={rows}
          widths={widths}
          validationError={validationError}
          renderInput={renderInput}
          deleteRow={deleteRow}
          builder={builder}
          toggleLockRow={toggleLockRow}
          sumColumn={sumColumn}
          toggleLockCell={toggleLockCell}
          openSettings={openSettings}
          enableCellLock={enableCellLock}
          disabled={disabled}
        />
      )}

      {builder && activeRow && (
        <SettingsModal
          isOpen={!!activeRow}
          onClose={() => setActiveRow(null)}
          handleSave={saveSettings}
          initialData={activeRow.data}
          rowIdx={activeRow.rowIdx}
          type="Row"
        />
      )}
      {(builder || dynamicRows) && (
        <ButtonsWrapper>
          <AddButton onClick={addRow} disabled={disabled}>
            {addRowText
              ? addRowText
              : intl.formatMessage({ id: "InpuTable.addRow" })}
          </AddButton>
        </ButtonsWrapper>
      )}
    </Wrapper>
  );
};

export default injectIntl(InputTable);
